// Import third-party libraries
import { useCallback, useEffect, useState } from "react";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from "react-router-dom";

// Import our components
import AlertsPage from "./components/AlertsPage";
import BugReporter from "./components/BugReporter";
import ContentTabs from "./components/ContentTabs";
import DocumentList from "./components/DocumentList";
import Filters from "./components/Filters";
import Header from "./components/Header";
import LandingPage from "./components/LandingPage";
import LegislationFilters from "./components/LegislationFilters";
import LegislationList from "./components/LegislationList";
import LinksList from "./components/LinksList";
import AuthForm from "./components/Login";
import NotificationFilters from "./components/NotificationFilters";
import NotificationList from "./components/NotificationList";
import Pagination from "./components/Pagination";
import PaymentCancel from "./components/PaymentCancel";
import PaymentSuccess from "./components/PaymentSuccess";
import PrivateRoute from "./components/PrivateRoute";
import { Toaster } from "./components/Toaster";
import { TabsContent } from "./components/ui/tabs";
import { TooltipProvider } from "./components/ui/tooltip";

// Import contexts
import { AuthProvider } from "./contexts/AuthContext";
import { StripeProvider } from "./contexts/StripeContext";
import { ThemeProvider } from "./contexts/ThemeContext";

// Import services and types
import {
  fetchDocumentos,
  fetchLegislation,
  fetchNotifications,
} from "./services/api";
import {
  ApiFiltrosDocumento,
  Documento,
  FiltrosDocumento,
  FiltrosLegislation,
  FiltrosNotification,
  Legislation,
  Notification,
} from "./types";

// Número de documentos por página
const ITEMS_PER_PAGE = 10;

// Componente principal de la aplicación
function MainApp() {
  // Estado para la pestaña activa
  const [activeTab, setActiveTab] = useState<string>("disposiciones");

  // Estados para disposiciones y anuncios
  const [documentos, setDocumentos] = useState<Documento[]>([]);
  const [loadingDocumentos, setLoadingDocumentos] = useState(true);
  const [errorDocumentos, setErrorDocumentos] = useState<string | null>(null);
  const [filtrosDocumentos, setFiltrosDocumentos] = useState<FiltrosDocumento>(
    {}
  );
  const [currentPageDocumentos, setCurrentPageDocumentos] = useState(1);
  const [totalDocumentos, setTotalDocumentos] = useState(0);
  const [documentsPerPage, setDocumentsPerPage] = useState(ITEMS_PER_PAGE);
  const [searchTermDocumentos, setSearchTermDocumentos] = useState<string>("");
  // Añadir estado para controlar cuándo hacer la carga de datos
  const [shouldFetchDocumentos, setShouldFetchDocumentos] = useState(true);

  // Estados para notificaciones
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loadingNotifications, setLoadingNotifications] = useState(true);
  const [errorNotifications, setErrorNotifications] = useState<string | null>(
    null
  );
  const [filtrosNotifications, setFiltrosNotifications] =
    useState<FiltrosNotification>({});
  const [currentPageNotifications, setCurrentPageNotifications] = useState(1);
  const [totalNotifications, setTotalNotifications] = useState(0);
  const [totalPagesNotifications, setTotalPagesNotifications] = useState(1);
  const [notificationsPerPage, setNotificationsPerPage] =
    useState(ITEMS_PER_PAGE);
  const [searchTermNotifications, setSearchTermNotifications] =
    useState<string>("");

  // Estados para legislación consolidada
  const [legislation, setLegislation] = useState<Legislation[]>([]);
  const [loadingLegislation, setLoadingLegislation] = useState(true);
  const [errorLegislation, setErrorLegislation] = useState<string | null>(null);
  const [filtrosLegislation, setFiltrosLegislation] =
    useState<FiltrosLegislation>({});
  const [currentPageLegislation, setCurrentPageLegislation] = useState(1);
  const [totalLegislation, setTotalLegislation] = useState(0);
  const [totalPagesLegislation, setTotalPagesLegislation] = useState(1);
  const [legislationPerPage, setLegislationPerPage] = useState(ITEMS_PER_PAGE);
  const [searchTermLegislation, setSearchTermLegislation] =
    useState<string>("");

  // Cargar documentos
  const cargarDocumentos = useCallback(
    async (filters: FiltrosDocumento, source: string = "unknown") => {
      // ID único para esta carga para facilitar el rastreo
      const loadId = Math.random().toString(36).substring(2, 9);
      console.log(
        `📊 [${loadId}] Iniciando cargarDocumentos desde '${source}' con filtros:`,
        filters
      );

      setLoadingDocumentos(true);
      setErrorDocumentos(null);
      try {
        // Asegurarse de que limit y offset estén definidos en los filtros
        const filtersWithPagination = {
          ...filters,
          limit: filters.limit || documentsPerPage.toString(),
          offset:
            filters.offset ||
            ((currentPageDocumentos - 1) * documentsPerPage).toString(),
        };

        console.log(
          `🔧 [${loadId}] Filtros con paginación:`,
          filtersWithPagination
        );

        // Convert FiltrosDocumento to ApiFiltrosDocumento
        const apiFilters: Record<string, string> = {};

        // Convert arrays to comma-separated strings
        Object.entries(filtersWithPagination).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            if (value.length > 0) {
              apiFilters[key] = value.join(",");
            }
          }
          // Regular string values
          else if (
            value !== undefined &&
            value !== "" &&
            value !== "undefined"
          ) {
            apiFilters[key] = String(value);
          }
        });

        console.log(`🔄 [${loadId}] Filtros API preparados:`, apiFilters);

        // Actualizar el estado de paginación basado en los filtros
        if (apiFilters.limit) {
          const newLimit = parseInt(apiFilters.limit);
          console.log(`📏 [${loadId}] Actualizando limit a ${newLimit}`);
          setDocumentsPerPage(newLimit);
        }

        if (apiFilters.offset) {
          const newPage =
            Math.floor(
              parseInt(apiFilters.offset) / parseInt(apiFilters.limit || "10")
            ) + 1;
          console.log(`📄 [${loadId}] Actualizando página a ${newPage}`);
          setCurrentPageDocumentos(newPage);
        }

        console.log(`🌐 [${loadId}] Llamando a fetchDocumentos`);
        const response = await fetchDocumentos(
          apiFilters as unknown as ApiFiltrosDocumento
        );

        console.log(
          `✅ [${loadId}] Respuesta recibida. Documentos: ${response.items.length}, Total: ${response.total}`
        );
        setDocumentos(response.items || []);
        setTotalDocumentos(response.total || 0);
      } catch (err) {
        console.error(`❌ [${loadId}] Error cargando documentos:`, err);
        setErrorDocumentos(
          "Error al cargar los documentos. Por favor, inténtelo de nuevo más tarde."
        );
        setDocumentos([]);
      } finally {
        setLoadingDocumentos(false);
        console.log(`🏁 [${loadId}] Finalizada carga de documentos`);
      }
    },
    [currentPageDocumentos, documentsPerPage]
  );

  // Cargar notificaciones
  const cargarNotifications = useCallback(
    async (filters: FiltrosNotification) => {
      setLoadingNotifications(true);
      setErrorNotifications(null);
      try {
        // Asegurarse de que page y page_size estén incluidos en los filtros
        const filtersWithPagination = {
          ...filters,
          page: currentPageNotifications,
          page_size: notificationsPerPage,
        };

        const response = await fetchNotifications(filtersWithPagination);
        setNotifications(response.items || []);
        setTotalNotifications(response.total || 0);
        setTotalPagesNotifications(response.total_pages || 1);
      } catch (err) {
        console.error("Error cargando notificaciones:", err);
        setErrorNotifications(
          "Error al cargar las notificaciones. Por favor, inténtelo de nuevo más tarde."
        );
        setNotifications([]);
      } finally {
        setLoadingNotifications(false);
      }
    },
    [currentPageNotifications, notificationsPerPage]
  );

  // Cargar legislación consolidada
  const cargarLegislation = useCallback(
    async (filters: FiltrosLegislation) => {
      setLoadingLegislation(true);
      setErrorLegislation(null);
      try {
        // Asegurarse de que limit y offset estén incluidos en los filtros
        const filtersWithPagination = {
          ...filters,
          limit: legislationPerPage.toString(),
          offset: (
            (currentPageLegislation - 1) *
            legislationPerPage
          ).toString(),
        };

        const response = await fetchLegislation(filtersWithPagination);
        setLegislation(response.items || []);
        setTotalLegislation(response.total || 0);
        setTotalPagesLegislation(response.total_pages || 1);
      } catch (err) {
        console.error("Error cargando legislación:", err);
        setErrorLegislation(
          "Error al cargar la legislación consolidada. Por favor, inténtelo de nuevo más tarde."
        );
        setLegislation([]);
      } finally {
        setLoadingLegislation(false);
      }
    },
    [currentPageLegislation, legislationPerPage]
  );

  // Cargar datos según la pestaña activa
  useEffect(() => {
    // Para la pestaña de documentos, solo hacer la carga cuando shouldFetchDocumentos sea true
    if (activeTab === "disposiciones") {
      if (shouldFetchDocumentos) {
        console.log(
          "🔄 useEffect: Cargando documentos por activación de shouldFetchDocumentos"
        );
        cargarDocumentos(filtrosDocumentos, "useEffect-shouldFetch");
        // Resetear el estado para evitar cargas repetidas
        setShouldFetchDocumentos(false);
      } else {
        console.log("⏭️ useEffect: Ignorando - shouldFetchDocumentos es false");
      }
    } else if (activeTab === "notificaciones") {
      cargarNotifications(filtrosNotifications);
    } else if (activeTab === "legislacion") {
      cargarLegislation(filtrosLegislation);
    }
  }, [
    activeTab,
    shouldFetchDocumentos,
    currentPageNotifications,
    filtrosNotifications,
    currentPageLegislation,
    filtrosLegislation,
    cargarDocumentos,
    cargarLegislation,
    cargarNotifications,
    filtrosDocumentos,
  ]);

  // El siguiente efecto asegura que se active la carga cuando se cambia a la pestaña de documentos
  useEffect(() => {
    if (activeTab === "disposiciones") {
      console.log(
        "🔄 useEffect: Activando carga de documentos por cambio de tab"
      );
      setShouldFetchDocumentos(true);
    }
  }, [activeTab]);

  // Monitorear cambios en filtrosDocumentos solo para logging, no para activar cargas
  useEffect(() => {
    if (activeTab === "disposiciones") {
      console.log(
        "ℹ️ Cambio en filtrosDocumentos detectado:",
        filtrosDocumentos
      );
    }
  }, [filtrosDocumentos, activeTab]);

  // Manejar cambio de página para documentos
  const handlePageChangeDocumentos = (page: number) => {
    console.log(`📄 handlePageChangeDocumentos: Cambiando a página ${page}`);

    // Calcular el nuevo offset basado en la página y el tamaño de página actual
    const newOffset = (page - 1) * documentsPerPage;

    // Crear filtros con la nueva página
    const filtersWithPagination = {
      ...filtrosDocumentos,
      limit: documentsPerPage.toString(),
      offset: newOffset.toString(),
    };

    // Actualizar el estado local de la página actual
    setCurrentPageDocumentos(page);

    // Actualizar los filtros - esto no dispara la carga automáticamente
    setFiltrosDocumentos(filtersWithPagination);

    // Activar la carga
    console.log("🔄 handlePageChangeDocumentos: Activando carga de documentos");
    setShouldFetchDocumentos(true);

    // Actualizar URL para reflejar la nueva paginación
    if (window.history && window.history.pushState) {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set("limit", documentsPerPage.toString());
      searchParams.set("offset", newOffset.toString());

      const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
      window.history.pushState({ path: newUrl }, "", newUrl);
    }

    // Scroll al inicio de la tabla
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Manejar cambio de página para notificaciones
  const handlePageChangeNotifications = (page: number) => {
    setCurrentPageNotifications(page);
    // Scroll al inicio de la tabla
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Manejar cambio de página para legislación
  const handlePageChangeLegislation = (page: number) => {
    setCurrentPageLegislation(page);
    // Scroll al inicio de la tabla
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Manejar aplicación de filtros para documentos
  const handleApplyFiltersDocumentos = (filters: FiltrosDocumento) => {
    console.log("🔍 handleApplyFiltersDocumentos: Aplicando filtros", filters);

    // Guardar el término de búsqueda
    setSearchTermDocumentos(filters.texto || "");

    // Actualizar los filtros
    setFiltrosDocumentos(filters);
    setCurrentPageDocumentos(1); // Resetear a la primera página cuando se aplican nuevos filtros

    // Activar la carga
    console.log(
      "🔄 handleApplyFiltersDocumentos: Activando carga de documentos"
    );
    setShouldFetchDocumentos(true);
  };

  // Manejar aplicación de filtros para notificaciones
  const handleApplyFiltersNotifications = (filters: FiltrosNotification) => {
    // Guardar el término de búsqueda
    setSearchTermNotifications(filters.search_text || "");

    // Otras operaciones con los filtros...
    setFiltrosNotifications(filters);
    setCurrentPageNotifications(1); // Resetear a la primera página cuando se aplican nuevos filtros
  };

  // Manejar aplicación de filtros para legislación
  const handleApplyFiltersLegislation = (filters: FiltrosLegislation) => {
    // Guardar el término de búsqueda
    setSearchTermLegislation(filters.search_text || "");

    // Otras operaciones con los filtros...
    setFiltrosLegislation(filters);
    setCurrentPageLegislation(1); // Resetear a la primera página cuando se aplican nuevos filtros
  };

  const totalPagesDocumentos = Math.ceil(totalDocumentos / documentsPerPage);

  const handleItemsPerPageChangeDocumentos = (newItemsPerPage: number) => {
    console.log(
      `📏 handleItemsPerPageChangeDocumentos: Cambiando a ${newItemsPerPage} items por página`
    );

    // Guardar la nueva preferencia en localStorage
    localStorage.setItem("preferredLimit", newItemsPerPage.toString());

    // Usar el nuevo valor directamente para la llamada a la API
    const newOffset = 0; // Resetear a la primera página

    // Actualizar el estado
    setDocumentsPerPage(newItemsPerPage);
    setCurrentPageDocumentos(1);

    // Crear una copia de los filtros actuales y actualizar los valores de paginación
    const updatedFilters: FiltrosDocumento = {
      ...filtrosDocumentos,
      limit: newItemsPerPage.toString(),
      offset: newOffset.toString(),
    };

    console.log(
      "🔧 handleItemsPerPageChangeDocumentos: Actualizando filtros con nueva paginación",
      updatedFilters
    );

    // Actualizar los filtros
    setFiltrosDocumentos(updatedFilters);

    // Activar la carga
    console.log(
      "🔄 handleItemsPerPageChangeDocumentos: Activando carga de documentos"
    );
    setShouldFetchDocumentos(true);

    // Actualizar la URL para reflejar los nuevos parámetros de paginación
    if (window.history && window.history.pushState) {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set("limit", newItemsPerPage.toString());
      searchParams.set("offset", newOffset.toString());

      const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
      window.history.pushState({ path: newUrl }, "", newUrl);
    }
  };

  const handleItemsPerPageChangeNotifications = (newItemsPerPage: number) => {
    // Actualizar el estado
    setNotificationsPerPage(newItemsPerPage);
    setCurrentPageNotifications(1);

    // Cargar notificaciones con el nuevo tamaño de página
    cargarNotifications({
      ...filtrosNotifications,
      page: 1,
      page_size: newItemsPerPage,
    });
  };

  const handleItemsPerPageChangeLegislation = (newItemsPerPage: number) => {
    // Actualizar el estado
    setLegislationPerPage(newItemsPerPage);
    setCurrentPageLegislation(1);

    // Cargar legislación con el nuevo tamaño de página
    cargarLegislation({
      ...filtrosLegislation,
      limit: newItemsPerPage.toString(),
      offset: "0",
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <TooltipProvider delayDuration={0}>
        <main className="container mx-auto px-4 py-8">
          <div className="grid gap-6">
            {/* Tabs para cambiar entre secciones */}
            <ContentTabs activeTab={activeTab} onTabChange={setActiveTab}>
              {/* Contenido de Disposiciones y Anuncios */}
              <TabsContent value="disposiciones" className="space-y-6">
                {/* Sección de filtros */}
                <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
                  <div className="p-6">
                    <Filters onApplyFilters={handleApplyFiltersDocumentos} />
                  </div>

                  {errorDocumentos && (
                    <div className="mx-6 mb-6 p-4 rounded-lg bg-destructive/10 text-destructive">
                      {errorDocumentos}
                    </div>
                  )}
                </div>

                {/* Sección de resultados */}
                <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
                  <DocumentList
                    documentos={documentos}
                    loading={loadingDocumentos}
                    searchTerm={searchTermDocumentos}
                    onPageChange={handlePageChangeDocumentos}
                    onItemsPerPageChange={handleItemsPerPageChangeDocumentos}
                    totalItems={totalDocumentos}
                    currentPage={currentPageDocumentos}
                    itemsPerPage={documentsPerPage}
                  />

                  {!loadingDocumentos &&
                    !errorDocumentos &&
                    totalPagesDocumentos > 1 && (
                      <Pagination
                        currentPage={currentPageDocumentos}
                        totalItems={totalDocumentos}
                        itemsPerPage={documentsPerPage}
                        onPageChange={handlePageChangeDocumentos}
                        onItemsPerPageChange={
                          handleItemsPerPageChangeDocumentos
                        }
                      />
                    )}
                </div>
              </TabsContent>

              {/* Contenido de Notificaciones */}
              <TabsContent value="notificaciones" className="space-y-6">
                {/* Sección de filtros para notificaciones */}
                <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
                  <div className="p-6">
                    <NotificationFilters
                      onApplyFilters={handleApplyFiltersNotifications}
                    />
                  </div>

                  {errorNotifications && (
                    <div className="mx-6 mb-6 p-4 rounded-lg bg-destructive/10 text-destructive">
                      {errorNotifications}
                    </div>
                  )}
                </div>

                {/* Sección de resultados de notificaciones */}
                <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
                  <NotificationList
                    notifications={notifications}
                    loading={loadingNotifications}
                    searchTerm={searchTermNotifications}
                  />

                  {!loadingNotifications &&
                    !errorNotifications &&
                    totalPagesNotifications > 1 && (
                      <Pagination
                        currentPage={currentPageNotifications}
                        totalItems={totalNotifications}
                        itemsPerPage={notificationsPerPage}
                        onPageChange={handlePageChangeNotifications}
                        onItemsPerPageChange={
                          handleItemsPerPageChangeNotifications
                        }
                      />
                    )}
                </div>
              </TabsContent>

              {/* Contenido de Legislación Consolidada */}
              <TabsContent value="legislacion" className="space-y-6">
                {/* Sección de filtros para legislación */}
                <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
                  <div className="p-6">
                    <LegislationFilters
                      onApplyFilters={handleApplyFiltersLegislation}
                    />
                  </div>

                  {errorLegislation && (
                    <div className="mx-6 mb-6 p-4 rounded-lg bg-destructive/10 text-destructive">
                      {errorLegislation}
                    </div>
                  )}
                </div>

                {/* Sección de resultados de legislación */}
                <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
                  <LegislationList
                    legislation={legislation}
                    loading={loadingLegislation}
                    searchTerm={searchTermLegislation}
                  />

                  {!loadingLegislation &&
                    !errorLegislation &&
                    totalPagesLegislation > 1 && (
                      <Pagination
                        currentPage={currentPageLegislation}
                        totalItems={totalLegislation}
                        itemsPerPage={legislationPerPage}
                        onPageChange={handlePageChangeLegislation}
                        onItemsPerPageChange={
                          handleItemsPerPageChangeLegislation
                        }
                      />
                    )}
                </div>
              </TabsContent>

              {/* Contenido de Enlaces de Interés */}
              <TabsContent value="links" className="space-y-6">
                {/* Sección de enlaces de interés */}
                <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
                  <LinksList />
                </div>
              </TabsContent>
            </ContentTabs>
          </div>
        </main>
      </TooltipProvider>
    </div>
  );
}

function App() {
  return (
    <StripeProvider>
      <AuthProvider>
        <Router>
          <ThemeProvider>
            <TooltipProvider>
              <Routes>
                {/* Landing Page como ruta pública principal */}
                <Route path="/" element={<LandingPage />} />

                {/* Rutas de autenticación */}
                <Route path="/auth" element={<AuthForm />} />

                {/* Dashboard principal protegido */}
                <Route
                  path="/dashboard"
                  element={
                    <PrivateRoute>
                      <MainApp />
                    </PrivateRoute>
                  }
                />

                {/* Ruta para alertas personalizadas */}
                <Route
                  path="/alertas"
                  element={
                    <PrivateRoute>
                      <div className="flex flex-col min-h-screen">
                        <Header />
                        <main className="flex-grow">
                          <AlertsPage />
                        </main>
                      </div>
                    </PrivateRoute>
                  }
                />

                {/* Redirigir /login y /register a /auth */}
                <Route path="/login" element={<Navigate to="/auth" />} />
                <Route path="/register" element={<Navigate to="/auth" />} />

                {/* Payment routes */}
                <Route path="/payment-success" element={<PaymentSuccess />} />
                <Route path="/payment-cancel" element={<PaymentCancel />} />

                {/* Redirigir rutas no encontradas a landing */}
                <Route path="*" element={<Navigate to="/" />} />
              </Routes>

              {/* Bug Reporter component */}
              <BugReporter />

              {/* Toaster para notificaciones */}
              <Toaster />
            </TooltipProvider>
          </ThemeProvider>
        </Router>
      </AuthProvider>
    </StripeProvider>
  );
}

export default App;
